import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    IconButton,
    InputAdornment,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    Alert,
    CircularProgress
} from '@mui/material';
import { IconCopy, IconTrash, IconCheck } from '@tabler/icons-react';
import {
    useGetStorageFileSharesQuery,
    useCreateStorageFileShareMutation,
    useDeleteStorageFileShareMutation
} from 'store/slices/apiSlice';

const FileSharingDialog = ({ open, onClose, fileId, fileName }) => {
    const { data: sharesData, isLoading: loading, refetch: refetchShares } = useGetStorageFileSharesQuery(fileId, {
        skip: !open || !fileId
    });
    const [createShare, { isLoading: creating }] = useCreateStorageFileShareMutation();
    const [deleteShare] = useDeleteStorageFileShareMutation();
    const shares = sharesData ?? [];
    const [newShare, setNewShare] = useState({
        expires_in: 3600,
        password: '',
        max_downloads: null
    });
    const [shareUrl, setShareUrl] = useState('');
    const [copied, setCopied] = useState(false);

    const handleCreateShare = async () => {
        try {
            const data = await createShare({
                fileId,
                expires_in: newShare.expires_in,
                password: newShare.password || null,
                max_downloads: newShare.max_downloads || null
            }).unwrap();
            setShareUrl(data?.share_url ?? '');
            refetchShares();
            setNewShare({ expires_in: 3600, password: '', max_downloads: null });
        } catch (error) {
            console.error('Error creating share:', error);
        }
    };

    const handleCopyUrl = (url) => {
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleRevokeShare = async (shareId) => {
        if (!window.confirm('Are you sure you want to revoke this share?')) return;
        try {
            await deleteShare({ fileId, shareId }).unwrap();
            refetchShares();
        } catch (error) {
            console.error('Error revoking share:', error);
        }
    };

    const formatExpiresAt = (expiresAt) => {
        if (!expiresAt) return 'Never';
        const date = new Date(expiresAt);
        return date.toLocaleString();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Share File: {fileName}
            </DialogTitle>
            <DialogContent>
                {shareUrl && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Share created successfully!
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                                fullWidth
                                size="small"
                                value={shareUrl}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
                                                <IconButton onClick={() => handleCopyUrl(shareUrl)}>
                                                    {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Box>
                    </Alert>
                )}

                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Create New Share</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Expires In (seconds)"
                            type="number"
                            value={newShare.expires_in}
                            onChange={(e) => setNewShare({ ...newShare, expires_in: parseInt(e.target.value) || 3600 })}
                            helperText="1 hour = 3600 seconds"
                        />
                        <TextField
                            label="Password (optional)"
                            type="password"
                            value={newShare.password}
                            onChange={(e) => setNewShare({ ...newShare, password: e.target.value })}
                            helperText="Leave empty for no password"
                        />
                        <TextField
                            label="Max Downloads (optional)"
                            type="number"
                            value={newShare.max_downloads || ''}
                            onChange={(e) => setNewShare({ ...newShare, max_downloads: e.target.value ? parseInt(e.target.value) : null })}
                            helperText="Leave empty for unlimited"
                        />
                        <Button
                            variant="contained"
                            onClick={handleCreateShare}
                            disabled={creating}
                        >
                            {creating ? 'Creating...' : 'Create Share'}
                        </Button>
                    </Box>
                </Box>

                <Typography variant="h6" gutterBottom>Existing Shares</Typography>
                {loading ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : shares.length === 0 ? (
                    <Typography color="textSecondary">No shares created yet</Typography>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Share URL</TableCell>
                                    <TableCell>Expires</TableCell>
                                    <TableCell>Downloads</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {shares.map((share) => (
                                    <TableRow key={share.id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <TextField
                                                    size="small"
                                                    value={share.share_url}
                                                    InputProps={{ readOnly: true }}
                                                    sx={{ flex: 1 }}
                                                />
                                                <Tooltip title="Copy URL">
                                                    <IconButton size="small" onClick={() => handleCopyUrl(share.share_url)}>
                                                        <IconCopy size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{formatExpiresAt(share.expires_at)}</TableCell>
                                        <TableCell>
                                            {share.max_downloads
                                                ? `${share.download_count}/${share.max_downloads}`
                                                : share.download_count}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={share.is_active ? 'Active' : 'Revoked'}
                                                color={share.is_active ? 'success' : 'default'}
                                                size="small"
                                            />
                                            {share.password_protected && (
                                                <Chip label="Password" color="warning" size="small" sx={{ ml: 1 }} />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Revoke">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleRevokeShare(share.id)}
                                                >
                                                    <IconTrash size={16} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default FileSharingDialog;

